/**
 * Incremental SHA-256.
 *
 * `crypto.subtle.digest` is fast and native but one-shot: it needs the entire
 * input in memory. Content-addressed storage means every upload must be hashed
 * before it starts, and reading a 5 GB video into an ArrayBuffer to do that is
 * not an option on a phone.
 *
 * So: the native path for small inputs, and this streaming implementation for
 * large ones, at constant memory. Both produce identical digests — there is a
 * test that asserts exactly that, alongside the FIPS 180-4 vectors.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export class Sha256 {
  private readonly state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly buffer = new Uint8Array(64);
  private readonly words = new Uint32Array(64);
  private bufferLength = 0;
  private totalLength = 0;

  update(chunk: Uint8Array): this {
    this.totalLength += chunk.length;
    let offset = 0;

    // Top up a partial block first.
    if (this.bufferLength > 0) {
      const needed = Math.min(64 - this.bufferLength, chunk.length);
      this.buffer.set(chunk.subarray(0, needed), this.bufferLength);
      this.bufferLength += needed;
      offset = needed;

      if (this.bufferLength === 64) {
        this.compress(this.buffer, 0);
        this.bufferLength = 0;
      }
    }

    // Then whole blocks straight from the input, with no copying.
    while (offset + 64 <= chunk.length) {
      this.compress(chunk, offset);
      offset += 64;
    }

    // Whatever is left starts the next partial block.
    if (offset < chunk.length) {
      this.buffer.set(chunk.subarray(offset), 0);
      this.bufferLength = chunk.length - offset;
    }

    return this;
  }

  digest(): Uint8Array {
    const bitLength = this.totalLength * 8;

    // Padding: 0x80, then zeroes, then the 64-bit big-endian bit count.
    const padded = new Uint8Array(this.bufferLength < 56 ? 64 : 128);
    padded.set(this.buffer.subarray(0, this.bufferLength));
    padded[this.bufferLength] = 0x80;

    const view = new DataView(padded.buffer);
    // Lengths beyond 2^53 bits are not representable in a JS number, and are
    // also not a file anyone is uploading.
    view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
    view.setUint32(padded.length - 4, bitLength >>> 0);

    for (let offset = 0; offset < padded.length; offset += 64) {
      this.compress(padded, offset);
    }

    const out = new Uint8Array(32);
    const outView = new DataView(out.buffer);
    for (let i = 0; i < 8; i++) outView.setUint32(i * 4, this.state[i]!);
    return out;
  }

  hex(): string {
    return bytesToHex(this.digest());
  }

  private compress(block: Uint8Array, offset: number): void {
    const w = this.words;

    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4;
      w[i] = ((block[j]! << 24) | (block[j + 1]! << 16) | (block[j + 2]! << 8) | block[j + 3]!) >>> 0;
    }
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15]!;
      const b = w[i - 2]!;
      const s0 = (rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3)) >>> 0;
      const s1 = (rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10)) >>> 0;
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }

    let a = this.state[0]!;
    let b = this.state[1]!;
    let c = this.state[2]!;
    let d = this.state[3]!;
    let e = this.state[4]!;
    let f = this.state[5]!;
    let g = this.state[6]!;
    let h = this.state[7]!;

    for (let i = 0; i < 64; i++) {
      const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const temp1 = (h + S1 + ch + K[i]! + w[i]!) >>> 0;
      const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    this.state[0] = (this.state[0]! + a) >>> 0;
    this.state[1] = (this.state[1]! + b) >>> 0;
    this.state[2] = (this.state[2]! + c) >>> 0;
    this.state[3] = (this.state[3]! + d) >>> 0;
    this.state[4] = (this.state[4]! + e) >>> 0;
    this.state[5] = (this.state[5]! + f) >>> 0;
    this.state[6] = (this.state[6]! + g) >>> 0;
    this.state[7] = (this.state[7]! + h) >>> 0;
  }
}

function rotr(value: number, bits: number): number {
  return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}

export function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (const byte of bytes) out += byte.toString(16).padStart(2, "0");
  return out;
}

/** Inputs at or below this go through the native one-shot digest. */
export const NATIVE_DIGEST_LIMIT = 64 * 1024 * 1024;

/**
 * Hash a Blob/File, choosing the cheapest correct strategy.
 *
 * `onProgress` reports 0–1 so a large video does not look frozen while it is
 * being read — hashing a 3 GB file takes a noticeable moment.
 */
export async function hashBlob(
  blob: Blob,
  options: { chunkSize?: number; onProgress?: (ratio: number) => void } = {},
): Promise<string> {
  if (blob.size <= NATIVE_DIGEST_LIMIT && globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
    options.onProgress?.(1);
    return bytesToHex(new Uint8Array(digest));
  }

  const chunkSize = options.chunkSize ?? 8 * 1024 * 1024;
  const hash = new Sha256();
  let offset = 0;

  while (offset < blob.size) {
    const end = Math.min(offset + chunkSize, blob.size);
    const chunk = new Uint8Array(await blob.slice(offset, end).arrayBuffer());
    hash.update(chunk);
    offset = end;
    options.onProgress?.(offset / blob.size);
  }

  return hash.hex();
}
